import axios from "axios";
import CONF from "@/Config.js";
import router from "@/router";
import {useLoading} from 'vue-loading-overlay';

const $loading = useLoading({
    // options
});

/**
 * initial state
 * 사용 예) this.$store.state.auth.is_login
 */
const state = {
  access_token: "", // 토큰 정보
  user_info: null, // 로그인 사용자 정보
  role_info: null, // 역할 정보 { role_code, role_name }
  page_auth_info: null, // 페이지 접근권한 정보 (URI 배열)
  is_login: false, // 로그인 여부
  permissionCachedAt: null, // 권한 캐시 시간
  permissionCacheTTL: 300000 // 5분 (밀리초)
};

/**
 * getters
 * 사용 예) this.$store.getters['auth/getUserInfo'];
 */
const getters = {
  getAccessToken: (state) => state.access_token,
  getIsLogin: (state) => state.is_login,
  getUserInfo: (state) => state.user_info,
  getRoleInfo: (state) => state.role_info,
  getRoleCode: (state) => state.role_info?.role_code,
  getRoleName: (state) => state.role_info?.role_name,
  isSuperAdmin: (state) => state.role_info?.role_code === 'super_admin',

  // 페이지 접근 권한 체크 (개선된 버전)
  getPageAuthInfo: (state) => (to_path) => {
    //console.log('🔍 [Permission Check]', {
    //  to_path,
    //  role_code: state.role_info?.role_code,
    //  page_auth_info: state.page_auth_info
    //});

    // super_admin은 모든 페이지 접근 가능
    if (state.role_info?.role_code === 'super_admin') {
      //console.log('✅ [Permission] super_admin - 모든 페이지 접근 허용');
      return true;
    }

    // dashboard는 모든 인증된 사용자 접근 가능
    if (to_path === '/dashboards/dashboard-default' || to_path === '/dashboards/sales') {
      //console.log('✅ [Permission] Dashboard 페이지 - 모든 사용자 접근 허용');
      return true;
    }

    // 내 정보 페이지는 모든 인증된 사용자 접근 가능
    if (to_path === '/pages/profile/overview' || to_path.startsWith('/pages/profile/overview/')) {
      //console.log('✅ [Permission] 내 정보 페이지 - 모든 사용자 접근 허용');
      return true;
    }

    // page_auth_info가 null이거나 배열이 아니면 false
    if (!state.page_auth_info || !Array.isArray(state.page_auth_info)) {
      //console.warn('❌ [Permission] No page_auth_info available:', state.page_auth_info);
      return false;
    }

    // 정확한 경로 매칭 또는 하위 경로 매칭
    const hasAccess = state.page_auth_info.some(uri => {
      // 정확히 일치하는 경우
      if (to_path === uri) {
        //console.log(`✅ [Permission] 정확히 일치: ${to_path} === ${uri}`);
        return true;
      }
      // 하위 경로인 경우 (예: uri가 /users이고 to_path가 /users/managers인 경우)
      // 단, uri 뒤에 /가 붙어야 함 (예: /user가 /users를 매칭하지 않도록)
      if (to_path.startsWith(uri + '/')) {
        //console.log(`✅ [Permission] 하위 경로 매칭: ${to_path} startsWith ${uri}/`);
        return true;
      }
      return false;
    });

    if (!hasAccess) {
      console.warn(`❌ [Permission] Access denied to ${to_path}. Available URIs:`, state.page_auth_info);
    }

    return hasAccess;
  },

  // 메뉴 필터링용 권한 체크
  hasPermission: (state) => (pageUri) => {
    // super_admin은 모든 메뉴 표시
    if (state.role_info?.role_code === 'super_admin') {
      return true;
    }

    // dashboard는 항상 허용
    if (pageUri === '/dashboards/dashboard-default' || pageUri === '/dashboards/sales') {
      return true;
    }

    // 내 정보 페이지는 항상 허용
    if (pageUri === '/pages/profile/overview') {
      return true;
    }

    // page_auth_info 확인
    if (!state.page_auth_info || !Array.isArray(state.page_auth_info)) {
      return false;
    }

    // 정확한 경로 매칭
    return state.page_auth_info.includes(pageUri);
  }
};

/**
 * mutations
 * 사용 예) this.$store.commit('auth/logout','');
 */
const mutations = {
  login(state, payload) {
    state.access_token = payload.token;
    state.user_info = payload.user_info;
    state.is_login = true;
  },
  logout(state) {
    state.access_token = "";
    state.user_info = null;
    state.role_info = null;
    state.page_auth_info = null;
    state.is_login = false;
    state.permissionCachedAt = null;
  },
  refreshToken(state, payload) {
    state.access_token = payload;
  },
  setRoleInfo(state, payload) {
    state.role_info = payload;
  },
  setPageAuthInfo(state, payload) {
    state.page_auth_info = payload;
  },
  setPermissionCachedAt(state, timestamp) {
    state.permissionCachedAt = timestamp;
  },
  // 하위 호환성을 위해 기존 pageAuthInfo mutation 유지
  pageAuthInfo(state, payload) {
    state.page_auth_info = payload;
  }
};

/**
 * actions
 * 사용 예) this.$store.dispatch('auth/logout');
 */
const actions = {
  // 로그인 -> 토큰 반환
  login({ dispatch }, { email, password }) {
    const loader = $loading.show();

    axios
      .post(`${CONF.API_URL}/auth/emp/token`, {
        userEmail: email,
        password,
      })
      .then((res) => {
        if (res.status === 200) {
          loader.hide();

          const loadDt = new Date();
          const expires_in = res.data.expires_in;
          const expire_time = new Date(Date.parse(loadDt) + 1000 * expires_in);

          localStorage.setItem("access_token", res.data.access_token);
          localStorage.setItem("expire_time", expire_time.toISOString());

          dispatch("getMemberInfo");
        }
      })
      .catch((err) => {
        alert("로그인 실패"); // 직접 메시지 추가
        loader.hide();
        console.error(err);
      });
  },

  // 사용자 정보 가져오기 (Promise 반환 추가)
  async getMemberInfo({ commit, dispatch }) {
    const token = localStorage.getItem("access_token");
    if (!token || token === "null") return;

    try {
      const response = await axios.get(`${CONF.API_URL}/auth/emp/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const user_info = {
        idx: response.data.userIdx,
        name: response.data.userName,
        email: response.data.userEmail,
      };

      // 역할 정보 저장 (백엔드에서 role_code, role_name 제공 시)
      if (response.data.role_code && response.data.role_name) {
        commit("setRoleInfo", {
          role_code: response.data.role_code,
          role_name: response.data.role_name,
        });
      }

      commit("login", { token, user_info });

      // 페이지 권한 로드
      await dispatch("loadPagePermissions");

      // 현재 경로 확인 후 리다이렉트 처리
      const currentRoute = router.currentRoute.value;
      const redirect = currentRoute.query.redirect;

      if (currentRoute.name === "Signin Cover" && redirect) {
        router.push(redirect);
      } else if (currentRoute.name === "Signin Cover") {
        router.push({ name: "Sales" });
      }
    } catch (err) {
      alert("사용자 정보를 가져올 수 없습니다.");
      router.push({ name: "Signin Cover" }); // 로그인 페이지로 이동
    }
  },

  // 페이지 권한 로드 (캐싱 적용)
  async loadPagePermissions({ commit, state }) {
    const now = Date.now();

    // 캐시가 유효하면 API 호출 생략
    if (
      state.permissionCachedAt &&
      now - state.permissionCachedAt < state.permissionCacheTTL
    ) {
      return;
    }

    try {
      const response = await this.$axios({
        method: 'get',
        url: '/roles/pagePermission/user/pages',
      });

      // 백엔드에서 pages 배열 반환 시 URI만 추출
      const pageUris = response.data.pages?.map((p) => p.page_uri) || [];
      commit("setPageAuthInfo", pageUris);
      commit("setPermissionCachedAt", now);
    } catch (error) {
      console.error("Failed to load page permissions:", error);
      // 권한 로드 실패 시 빈 배열로 설정 (접근 불가)
      commit("setPageAuthInfo", []);
    }
  },

  // 권한 강제 새로고침
  async forceRefreshPermissions({ commit, dispatch }) {
    commit("setPermissionCachedAt", null);
    await dispatch("loadPagePermissions");
  },

  refreshToken({ commit }) {
    const token = localStorage.getItem("access_token");

    axios
      .get(`${CONF.API_URL}/auth/emp/refresh`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.status === 200) {
          const loadDt = new Date();
          const expires_in = res.data.expires_in;
          const expire_time = new Date(Date.parse(loadDt) + 1000 * expires_in);
          const refresh_token = res.data.access_token;

          localStorage.setItem("access_token", refresh_token);
          localStorage.setItem("expire_time", expire_time.toISOString());

          commit("refreshToken", refresh_token);
        }
      })
      .catch(() => {
        alert("토큰 갱신 실패");
      });
  },

  logout({ commit }) {
    localStorage.removeItem("access_token");
    commit("logout");

    router.push({ name: "Signin Cover" });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
